import {
  EAWSRegion,
  ESdChangeReason,
  IAwsSettings,
  ICommonSettings,
  IExchangeServerMailSettings,
  ISmtpSettings
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

/** The fields inside this dialog, for the settings search. See `IConfigFieldProps.searchTerms`. */
export const MAIL_SETTINGS_SEARCH_TERMS: string[] = [
  "Type",
  "Server Address",
  "Server Port",
  "SMTP User Name",
  "E-mail Address",
  "E-mail Password",
  "Is SSL Enabled",
  "Validate Server Certificate",
  "Access Key Id",
  "Server Access Key",
  "Default From Email",
  "Region",
  "Username",
  "Auto Discover Server Address"
];

export interface IConfigMailSettingsState {
  type?: "smtp" | "aws" | "exchangeServerMail";
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingMail?: ISmtpSettings | IAwsSettings | IExchangeServerMailSettings;
}

export interface IConfigMailSettingsProps {
  isWorkflowMailSetting?: boolean;
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingMailId?: string;
  editingMailType?: "smtp" | "aws" | "exchangeServerMail";
  commonSettings: ICommonSettings;
  getSectionDescription: (conifgKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

class ConfigMailSettings extends PureSdComponent<IConfigMailSettingsProps & IWithSDProps, IConfigMailSettingsState> {
  formRef = React.createRef<FormInstance>();

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigMailSettingsProps & IWithSDProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingMail: undefined
    };
  }

  componentDidMount() {
    this.loadMailSettings();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigMailSettingsProps & IWithSDProps) {
    if (prevProps.editingMailId && !this.props.editingMailId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.setState({ type: undefined });
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingMailId !== this.props.editingMailId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.setState({ type: undefined });

      this.loadMailSettings();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.setState({ type: undefined });

      this.loadMailSettings();
    }
  }

  loadMailSettings = () => {
    if (!this.props.editingMailId || !this.props.editingMailType) return;
    let mailSetting: any;

    if (this.props.editingMailType === "smtp") {
      mailSetting = (this.props.commonSettings.smtpSettings || []).find(
        (x) => x.id === this.props.editingMailId && !x.isGlobalCommonConfigOnProjectConfig
      );
      if (mailSetting)
        this.setState({ type: "smtp" }, () => {
          this.formRef.current?.setFieldsValue({ ...mailSetting, type: "smtp" });
        });
      return;
    }
    if (this.props.editingMailType === "aws") {
      mailSetting = (this.props.commonSettings.awsSettings || []).find(
        (x) => x.id === this.props.editingMailId && !x.isGlobalCommonConfigOnProjectConfig
      );
      if (mailSetting) {
        this.setState({ type: "aws" }, () => {
          this.formRef.current?.setFieldsValue({ ...mailSetting, type: "aws" });
        });
      }

      return;
    }

    if (this.props.editingMailType === "exchangeServerMail") {
      mailSetting = (this.props.commonSettings.exchangeServerMailSettings || []).find(
        (x) => x.id === this.props.editingMailId && !x.isGlobalCommonConfigOnProjectConfig
      );
      if (mailSetting) {
        this.setState({ type: "exchangeServerMail" }, () => {
          this.formRef.current?.setFieldsValue({ ...mailSetting, type: "exchangeServerMail" });
        });
      }
      return;
    }
  };

  existingNameValidator = async (_rule, value) => {
    const { smtpSettings, awsSettings, exchangeServerMailSettings } = this.props.commonSettings;

    const isSettingAlreadyExist =
      [...(smtpSettings || []), ...(awsSettings || []), ...(exchangeServerMailSettings || [])].some(
        (x) => x.name === value && x.id !== this.props.editingMailId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Mail Setting Name is already exist.");
  };

  onFinish = (data: any) => {
    if (!this.state.type) return;

    this.saveStatus.start();
    const obj: any = {
      id:
        this.props.editingMailId && this.props.editingMailId !== ""
          ? this.props.editingMailId
          : Guid.create().toString(),
      name: data.name
    };

    if (this.state.type === "smtp") {
      obj.serverAddress = data.serverAddress?.trim();
      obj.serverPort = data.serverPort;
      obj.userName = data.userName?.trim();
      obj.emailAddress = data.emailAddress?.trim();
      obj.emailPassword = data.emailPassword;
      obj.enableSsl = data.enableSsl;
      obj.validateServerCertificate = data.validateServerCertificate ?? false;
    } else if (this.state.type === "aws") {
      obj.accessKeyId = data.accessKeyId?.trim();
      obj.secretAccessKey = data.secretAccessKey?.trim();
      obj.defaultFromEMail = data.defaultFromEMail?.trim();
      obj.selectedRegion = data.selectedRegion;
    } else if (this.state.type === "exchangeServerMail") {
      obj.serverAddress = data.serverAddress?.trim();
      obj.userName = data.userName?.trim();
      obj.emailAddress = data.emailAddress?.trim();
      obj.emailPassword = data.emailPassword?.trim();
      obj.autoDiscoverServerAddress = data.autoDiscoverServerAddress;
    }
    if (this.props.editingMailId) {
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        customizedId: `${this.state.type}|${obj.id}`,
        configKey: `${this.state.type}Settings`,
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        customizedId: `${this.state.type}|${obj.id}`,
        configKey: `${this.state.type}Settings`,
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteMail = () => {
    if (!this.props.editingMailId || !this.state.type) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    const childDataKey = this.props.formInputName[1] === "mailSettingsId" ? "workflowMainSettingsId" : "mailSettingsId";
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingMailId || "",
      configKey: `${this.state.type}Settings`,

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Mail setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      shouldControlOtherSections: {
        listOfControl: [["apiConfig", childDataKey]],
        onCannotDelete: () => {
          KMainFunctions.displayErrorNotification(`
          This Mail setting is
          used on App Settings ${
            this.props.formInputName[1] === "workflowMailSettingsId" ? "Workflow" : "Mail"
          } section. Please discard this setting
          on other config section before delete.
        `);
        }
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  getSectionDescription = (): React.ReactNode => {
    let contentKey = "emailsettingspopup_emailsetting_type";
    switch (this.state.type) {
      case "aws":
        contentKey = "emailsettingspopup_emailsettingsaws_description";
        break;
      case "smtp":
        contentKey = "emailsettingspopup_emailsettingssmtp_description";
        break;
      case "exchangeServerMail":
        contentKey = "emailsettingspopup_emailsettingsexchangeserver_pop_updescription";
        break;
    }
    return this.props.getSectionDescription(contentKey);
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Mail Setting"
        secondHeader="Mail Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingMailId ? this.deleteMail : undefined}
            deleteTestId="mailSettingsDeleteButton"
            submitTestId="mailSettingsCreateButton"
            submitLabel={this.props.editingMailId ? "UPDATE" : "CREATE"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configMailForm"
          />
        }
      >
        <Form
          id="configMailForm"
          name="mailSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.getSectionDescription()}>
            <div>
              <ConfigField
                label="Type"
                required
                tooltipContentKey={
                  !this.state.type
                    ? "emailsettingspopup_emailsetting_type"
                    : this.state.type === "smtp"
                      ? "emailsettingspopup_emailsettingssmtp_type"
                      : this.state.type === "aws"
                        ? "emailsettingspopup_emailsettingsaws_type"
                        : "emailsettingspopup_emailsettingsexchangeserver_type"
                }
              >
                <Form.Item name="type" rules={[{ required: true, message: "Please select a Mail Setting Type." }]}>
                  <KuikaSelectbox
                    size="md"
                    data-testid="mailSettingsTypeDropdown"
                    disabled={!!this.props.editingMailId}
                    onChange={(type) => {
                      this.setState({ type: type as "aws" | "smtp" | "exchangeServerMail" });
                    }}
                    options={[
                      { value: "smtp", label: "SMTP", testId: "smtpOption", searchLabel: "SMTP" },
                      { value: "aws", label: "AWS", testId: "awsOption", searchLabel: "AWS" },
                      {
                        value: "exchangeServerMail",
                        label: "Exchange Server",
                        testId: "exchangeservermailOption",
                        searchLabel: "Exchange Server"
                      }
                    ]}
                  />
                </Form.Item>
              </ConfigField>

              {this.state.type === "smtp" && (
                <>
                  <ConfigField label="Name" required tooltipContentKey="emailsettingspopup_emailsettingssmtp_name">
                    <Form.Item
                      name="name"
                      rules={[
                        { required: true, message: "Please enter a Mail Setting Name." },
                        { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Mail Setting Name is invalid" },
                        { validator: this.existingNameValidator }
                      ]}
                    >
                      <KuikaInput size="md" data-testid="mailSettingsNameInput" autoFocus />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField
                    label="Server Address"
                    tooltipContentKey="emailsettingspopup_emailsettingssmtp_serveraddress"
                  >
                    <Form.Item name="serverAddress">
                      <KuikaInput size="md" />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField label="Server Port" tooltipContentKey="emailsettingspopup_emailsettingssmtp_serverport">
                    <Form.Item name="serverPort">
                      <KuikaInput size="md" type="number" />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField label="SMTP User Name" tooltipContentKey="emailsettingspopup_emailsettingssmtp_username">
                    <Form.Item name="userName">
                      <KuikaInput size="md" />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField
                    label="E-mail Address"
                    tooltipContentKey="emailsettingspopup_emailsettingssmtp_emailadress"
                  >
                    <Form.Item name="emailAddress">
                      <KuikaInput size="md" />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField
                    label="E-mail Password"
                    tooltipContentKey="emailsettingspopup_emailsettingssmtp_emailpassword"
                  >
                    <Form.Item name="emailPassword">
                      <KuikaInput size="md" autoComplete="off" type="password" revealable />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField
                    label="Is SSL Enabled"
                    tooltipContentKey="emailsettingspopup_emailsettingssmtp_issslenabled"
                  >
                    <Form.Item name="enableSsl" valuePropName="checked">
                      <KuikaSwitch />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField
                    label="Validate Server Certificate"
                    tooltipContentKey="emailsettingspopup_emailsettingssmtp_validateservercertificate"
                  >
                    <Form.Item name="validateServerCertificate" valuePropName="checked">
                      <KuikaSwitch dataTestId="mailSettingsValidateServerCertificateSwitch" />
                    </Form.Item>
                  </ConfigField>
                </>
              )}

              {this.state.type === "aws" && (
                <>
                  <ConfigField label="Name" required tooltipContentKey="emailsettingspopup_emailsettingsaws_name">
                    <Form.Item
                      name="name"
                      rules={[
                        { required: true, message: "Please enter a Mail Setting Name." },
                        { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Mail Setting Name is invalid" },
                        { validator: this.existingNameValidator }
                      ]}
                    >
                      <KuikaInput size="md" data-testid="mailSettingsNameInput" autoFocus />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField
                    label="Access Key Id"
                    required
                    tooltipContentKey="emailsettingspopup_emailsettingsaws_accesskeyid"
                  >
                    <Form.Item
                      name="accessKeyId"
                      rules={[{ required: true, message: "Please enter a Access Key Id." }]}
                    >
                      <KuikaInput size="md" />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField
                    label="Server Access Key"
                    required
                    tooltipContentKey="emailsettingspopup_emailsettingsaws_serveraccesskey"
                  >
                    <Form.Item
                      name="secretAccessKey"
                      rules={[{ required: true, message: "Please enter a Server Access Key." }]}
                    >
                      <KuikaInput size="md" />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField
                    label="Default From Email"
                    required
                    tooltipContentKey="emailsettingspopup_emailsettingsaws_defaultfromemail"
                  >
                    <Form.Item
                      name="defaultFromEMail"
                      rules={[{ required: true, message: "Please enter a Default From Email." }]}
                    >
                      <KuikaInput size="md" />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField label="Region" required tooltipContentKey="emailsettingspopup_emailsettingsaws_region">
                    <Form.Item name="selectedRegion" rules={[{ required: true, message: "Please select a Region." }]}>
                      <KuikaSelectbox
                        size="md"
                        placeholder="Choose Region"
                        options={[
                          { value: EAWSRegion.USWest2, label: "US West 2" },
                          { value: EAWSRegion.EUWest3, label: "EU West 3" }
                        ]}
                      />
                    </Form.Item>
                  </ConfigField>
                </>
              )}

              {this.state.type === "exchangeServerMail" && (
                <>
                  <ConfigField
                    label="Name"
                    required
                    tooltipContentKey="emailsettingspopup_emailsettingsexchangeserver_name"
                  >
                    <Form.Item
                      name="name"
                      rules={[
                        { required: true, message: "Please enter a Mail Setting Name." },
                        { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Mail Setting Name is invalid" },
                        { validator: this.existingNameValidator }
                      ]}
                    >
                      <KuikaInput size="md" data-testid="mailSettingsNameInput" autoFocus />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField
                    label="Server Address"
                    required
                    tooltipContentKey="emailsettingspopup_emailsettingsexchangeserver_serveraddress"
                  >
                    <Form.Item
                      name="serverAddress"
                      rules={[{ required: true, message: "Please enter a Server Address." }]}
                    >
                      <KuikaInput size="md" />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField
                    label="Username"
                    required
                    tooltipContentKey="emailsettingspopup_emailsettingsexchangeserver_username"
                  >
                    <Form.Item name="userName" rules={[{ required: true, message: "Please enter a Username." }]}>
                      <KuikaInput size="md" />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField
                    label="E-mail Address"
                    required
                    tooltipContentKey="emailsettingspopup_emailsettingsexchangeserver_e_mailaddress"
                  >
                    <Form.Item
                      name="emailAddress"
                      rules={[{ required: true, message: "Please enter a Email Address." }]}
                    >
                      <KuikaInput size="md" />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField
                    label="E-mail Password"
                    required
                    tooltipContentKey="emailsettingspopup_emailsettingsexchangeserver_e_mailpassword"
                  >
                    <Form.Item
                      name="emailPassword"
                      rules={[{ required: true, message: "Please enter a Email Password." }]}
                    >
                      <KuikaInput size="md" autoComplete="off" type="password" revealable />
                    </Form.Item>
                  </ConfigField>
                  <ConfigField
                    label="Auto Discover Server Address"
                    tooltipContentKey="emailsettingspopup_emailsettingsexchangeserver_autodiscoverserveraddress"
                  >
                    <Form.Item name="autoDiscoverServerAddress" valuePropName="checked">
                      <KuikaSwitch />
                    </Form.Item>
                  </ConfigField>
                </>
              )}
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const configMailSettings = withSD<IConfigMailSettingsProps>(ConfigMailSettings);
export { configMailSettings as ConfigMailSettings };
